import type { ReactNode } from 'react';

/**
 * Reconstruct the raw markdown string for a nested `<Streamdown>` from the
 * children an outer Streamdown handed to a custom block tag (e.g.
 * `<keypoints>`, `<safetycallout>`).
 *
 * HTML blocks don't process markdown by default, so block components re-parse
 * their children with a nested Streamdown. By the time children reach the
 * handler the outer Streamdown may have already turned `<cite-badge>` markers
 * into React elements; we turn those back into `<cite-badge data-idx="…">`
 * marker strings so the nested Streamdown can re-render them with styling
 * (EVI-553). Other React elements are dropped — block tags receive string
 * content from the LLM.
 */
export function reconstructNestedMarkdown(children: ReactNode): string {
  if (!children) return '';
  if (typeof children === 'string') return children.trim();
  if (typeof children === 'number') return String(children);

  if (Array.isArray(children)) {
    return children
      .map((child): string => {
        if (typeof child === 'string') return child;
        if (typeof child === 'number') return String(child);

        // `ReactNode` widens an element's `props` to `any`, so read it through
        // `unknown` and narrow the marker to what can be interpolated.
        const props = (child as { props?: unknown } | null)?.props;
        if (props && typeof props === 'object') {
          const dataIdx = (props as Record<string, unknown>)['data-idx'];
          if (typeof dataIdx === 'string' || typeof dataIdx === 'number') {
            return `<cite-badge data-idx="${dataIdx}"></cite-badge>`;
          }
        }

        return '';
      })
      .join('')
      .trim();
  }

  return '';
}
